import React, { useEffect, useState } from "react";
import { useVersionCheck } from "../hooks/useVersionCheck";
import "./UpdateBanner.scss";

/**
 * UpdateBanner
 *
 * Shows a Paymo-style toast banner when a new version of the app has been deployed.
 * - Polls every 30 minutes via useVersionCheck.
 * - Shows a countdown to auto-reload (30 seconds).
 * - User can manually refresh immediately or dismiss to stop the auto-reload.
 */
const AUTO_RELOAD_SECONDS = 30;

const UpdateBanner = () => {
  const { updateAvailable, dismissUpdate } = useVersionCheck(
    5_000,                      // ⚠️ TEMP: 5 seconds for testing (change back to 30 * 60 * 1000)
    AUTO_RELOAD_SECONDS * 1000  // auto-reload after 30 seconds
  );
  const [countdown, setCountdown] = useState(AUTO_RELOAD_SECONDS);

  // Drive a visual countdown
  useEffect(() => {
    if (!updateAvailable) {
      setCountdown(AUTO_RELOAD_SECONDS);
      return;
    }
    setCountdown(AUTO_RELOAD_SECONDS);
    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(tick);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [updateAvailable]);

  if (!updateAvailable) return null;

  return (
    <div className="update-banner" role="alert" aria-live="polite">
      <div className="update-banner__icon">🚀</div>
      <div className="update-banner__text">
        <strong>Update available</strong>
        <span>Refreshing in {countdown}s…</span>
      </div>
      <button
        className="update-banner__btn"
        onClick={() => window.location.reload()}
        aria-label="Refresh now to get the latest version"
      >
        Refresh now
      </button>
      <button
        className="update-banner__dismiss"
        onClick={dismissUpdate}
        aria-label="Dismiss update notification"
        title="Dismiss"
      >
        ✕
      </button>
    </div>
  );
};

export default UpdateBanner;
